/**
 * UTM and Click ID Tracking Cookie logic.
 * Captures UTM parameters globally in a cookie, and updates it when different values are used.
 */

const cookieName = 'utm_parameters';
const trackedParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'source',
    'source_detail',
    'gclid',
    'msclkid',
    'fbclid',
    'li_fat_id',
    'gad_source',
    'gad_campaignid',
    'gbraid',
];

const KNOWN_LLMS = [
    { name: 'chatgpt.com', match: /(^|\.)chatgpt\.com$/i },
    { name: 'gemini.google.com', match: /(^|\.)gemini\.google\.com$/i },
    { name: 'claude.ai', match: /(^|\.)claude\.ai$/i },
    { name: 'perplexity.ai', match: /(^|\.)perplexity\.ai$/i },
    { name: 'copilot.com', match: /(^|\.)copilot\.(com|microsoft\.com)$/i },
];

const KNOWN_SEARCH_ENGINES = [
    { name: 'google.com', match: /(^|\.)google\./i },
    { name: 'bing.com', match: /(^|\.)bing\.com$/i },
    { name: 'brave.com', match: /(^|\.)brave\.com$/i },
    { name: 'yahoo.com', match: /(^|\.)yahoo\./i },
    { name: 'duckduckgo.com', match: /(^|\.)duckduckgo\.com$/i },
    { name: 'baidu.com', match: /(^|\.)baidu\.com$/i },
    { name: 'yandex.com', match: /(^|\.)yandex\./i },
    { name: 'ecosia.org', match: /(^|\.)ecosia\.org$/i },
    { name: 'msn.com', match: /(^|\.)msn\.com$/i },
];

/**
 * Checks whether a value contains litmos.com.
 * @param {string} val
 * @returns {boolean}
 */
function isLitmosDomain(val) {
    if (!val || typeof val !== 'string') {
        return false;
    }
    return /litmos\.com/i.test(val);
}

/**
 * Delete cookie across current path and root domain.
 * @param {string} name
 */
function deleteCookie(name) {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    const hostname = window.location.hostname;
    if (hostname.includes('.')) {
        const rootDomain = hostname.replace(/^www\./, '');
        document.cookie = `${name}=; path=/; domain=.${rootDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
}

/**
 * Get cookie value by name.
 * @param {string} name
 * @returns {string|null}
 */
function getCookie(name) {
    const value = `; ${document.cookie}`;
    let result = null;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        result = decodeURIComponent(parts.pop().split(';').shift());
    }
    if (result && isLitmosDomain(result)) {
        deleteCookie(name);
        return null;
    }
    return result;
}

/**
 * Set a cookie with specified name, value, and expiration in days.
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function setCookie(name, value, days) {
    // Never write a cookie with litmos.com values
    if (isLitmosDomain(value)) {
        return;
    }
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
    }
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax${secure}`;
}

/**
 * Extract and sanitize referring hostname, ignoring internal site referrers.
 * @returns {string}
 */
function getReferrerHostname() {
    if (!document.referrer) {
        return '';
    }
    try {
        const refUrl = new window.URL(document.referrer);
        const host = refUrl.hostname.toLowerCase();
        if (isLitmosDomain(host) || host === window.location.hostname.toLowerCase()) {
            return '';
        }
        return host;
    } catch (e) { // eslint-disable-line no-unused-vars
        return '';
    }
}

/**
 * Determine default UTM parameters based on referring domain.
 * @returns {{ utm_source: string, utm_medium: string }}
 */
function getDefaultUtmParameters() {
    const refHost = getReferrerHostname();

    // 1. If referring domain is NOT present: (direct) / (direct)
    if (!refHost) {
        return {
            utm_source: '(direct)',
            utm_medium: '(direct)',
        };
    }

    // 2. Check known LLMs first
    for (const llm of KNOWN_LLMS) {
        if (llm.match.test(refHost)) {
            return {
                utm_source: llm.name,
                utm_medium: 'ai-assistant',
            };
        }
    }

    // 3. Check known search engines
    for (const se of KNOWN_SEARCH_ENGINES) {
        if (se.match.test(refHost)) {
            return {
                utm_source: se.name,
                utm_medium: 'organic',
            };
        }
    }

    // 4. Other referring domain: <domain> / referral
    const cleanDomain = refHost.replace(/^www\./, '');
    return {
        utm_source: cleanDomain,
        utm_medium: 'referral',
    };
}

/**
 * Checks URL search parameters and updates tracking cookies.
 */
const utmTracking = () => {
    // Purge any preexisting standalone cookies containing litmos.com
    ['source', 'source_detail', 'utm_source', 'wp_utm_source', 'wp_utm_source_detail'].forEach((name) => {
        const val = getCookie(name);
        if (isLitmosDomain(val)) {
            deleteCookie(name);
        }
    });

    const urlParams = new window.URLSearchParams(window.location.search);

    // Retrieve and parse existing consolidated cookie or initialize empty object
    let cookieData = {};
    const existingCookie = getCookie(cookieName);
    if (existingCookie) {
        try {
            cookieData = JSON.parse(existingCookie);
        } catch (e) { // eslint-disable-line no-unused-vars
            cookieData = {};
        }
    }

    // Clean up any litmos.com values from consolidated cookieData
    let cleanedCookieData = false;
    for (const key of Object.keys(cookieData)) {
        if (isLitmosDomain(cookieData[key])) {
            delete cookieData[key];
            cleanedCookieData = true;
        }
    }

    // Check if a cookie has already been created
    const hasExistingUtmCookie = Boolean(
        (cookieData && (cookieData.utm_source || cookieData.utm_medium || Object.keys(cookieData).length > 0)) ||
        getCookie('utm_source') ||
        getCookie('utm_medium'),
    );

    // Check if any tracked parameter is present in the URL
    let hasTrackedParam = false;
    for (const param of trackedParams) {
        if (urlParams.has(param)) {
            hasTrackedParam = true;
            break;
        }
    }

    let isUpdated = false;

    // Logic:
    // If UTMs are present in the URL at session start, write to cookie as existing logic.
    // A new UTM-url should override these values. UTM values should always be preferred.
    if (hasTrackedParam) {
        for (const param of trackedParams) {
            if (urlParams.has(param)) {
                const urlVal = urlParams.get(param) || '';

                // Never track or store litmos.com values
                if (isLitmosDomain(urlVal)) {
                    continue;
                }

                const currentVal = cookieData[param] || '';

                if (urlVal !== currentVal) {
                    cookieData[param] = urlVal;
                    isUpdated = true;

                    // Update individual parameter cookie
                    setCookie(param, urlVal, 30);
                }
            }
        }
    } else if (!hasExistingUtmCookie) {
        // If no UTMs are present in the URL at session start, and NO cookie has already been created:
        // Set defaults according to referring domain
        const defaults = getDefaultUtmParameters();
        cookieData.utm_source = defaults.utm_source;
        cookieData.utm_medium = defaults.utm_medium;
        setCookie('utm_source', defaults.utm_source, 30);
        setCookie('utm_medium', defaults.utm_medium, 30);
        isUpdated = true;
    }

    // If there were any updates or cleanups, update the consolidated cookie
    if (isUpdated || cleanedCookieData) {
        setCookie(cookieName, JSON.stringify(cookieData), 30);
    }
};

export default utmTracking;
